import { Link } from "react-router-dom";
import { FaRegFolderOpen } from "react-icons/fa";
import { MdOutlineCalendarMonth } from "react-icons/md";
import { useState } from "react";

/* ===== STUDENT CHIP CARD ===== */
function StudentCard({ name, id, empty = false }) {
  const content = (
    <div
      className={`border rounded px-3 py-1 small shadow-sm ${
        empty ? "bg-white text-muted" : "bg-light text-dark"
      }`}
      style={{
        minWidth: "120px",
        textAlign: "center",
        whiteSpace: "nowrap",
        cursor: empty ? "default" : "pointer",
      }}
    >
      {empty ? " - " : name}
    </div>
  );

  // ✅ If no student ID → no navigation
  if (empty || !id) {
    return content;
  }

  // ✅ Navigate only when studentId exists
  return (
    <Link
      className="text-decoration-none"
      to={`/report/studentAttendanceReport?studentId=${id}`}
    >
      {content}
    </Link>
  );
}

/* ===== TEACHER TIMETABLE ===== */
function TeacherTimetable({
  weeklySlots = [],
  setSelectedDate,
  getData,
  selectedDate,
}) {
  const [slots, setSlots] = useState(weeklySlots);
  const [loading, setLoading] = useState(false);

  const handleClear = () => {
    setSelectedDate(null);
    setSlots(weeklySlots);
    getData();
  };

  return (
    <div className="card border-0 p-3 shadow-sm h-100">
      <div className="row align-items-center mb-3">
        <div className="col-md-6">
          <h6 className="mb-3">Timetable</h6>
        </div>

        <div className="col-md-6 d-flex justify-content-end gap-2">
          <input
            type="week"
            className="form-control form-control-sm"
            value={selectedDate || ""}
            onChange={(e) => setSelectedDate(e.target.value)}
          />

          <button
            type="button"
            className="btn btn-sm btn-border"
            onClick={handleClear}
          >
            Clear
          </button>

          <button
            type="button"
            className="btn btn-sm text-white"
            style={{ background: "#eb862a" }}
            onClick={getData}
          >
            Search
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered text-center align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th style={{ width: "20%" }}>Day</th>
              <th style={{ width: "25%" }}>Class Timing</th>
              <th style={{ width: "55%" }}>Students</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3}>Loading...</td>
              </tr>
            ) : slots.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-muted">
                  No timetable available
                </td>
              </tr>
            ) : (
              slots.map((slot, index) => (
                <tr key={index}>
                  <td className="fw-medium">{slot.day}</td>
                  <td>{slot.classTiming}</td>
                  <td>
                    <div
                      className="d-flex flex-column gap-2 align-items-center"
                      style={{ maxHeight: "90px", overflowY: "auto" }}
                    >
                      {slot.students?.length > 0
                        ? slot.students.map((s, i) => (
                            <StudentCard
                              key={i}
                              name={s.studentName}
                              id={s.studentId}
                            />
                          ))
                        : Array.from({ length: 2 }).map((_, i) => (
                            <StudentCard key={i} empty />
                          ))}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ===== TOP CARD (ICON OR VALUE) ===== */
function TopStatCard({
  title,
  value,
  icon: Icon,
  link,
  state,
  leaveBalanceCount,
}) {
  const displayValue = title === "Leave Balance" ? leaveBalanceCount : value;

  return (
    <div className="col-md-4 mb-3">
      <Link to={link} state={state} style={{ textDecoration: "none" }}>
        <div className="card border-0 shadow-sm h-100">
          <div className="card-body d-flex flex-column justify-content-center">
            <h6 className="text-secondary mb-1">{title}</h6>

            {Icon ? (
              <div className="fs-4 text-dark">
                <Icon />
              </div>
            ) : (
              <h4 className="fw-bold text-dark">{displayValue ?? 0}</h4>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
}

/* ===== TEACHER DASHBOARD ===== */
function TeacherDashboard({
  data,
  loading,
  setSelectedDate,
  getData,
  selectedDate,
}) {
  const teacherDashboard = data?.teacherDashboard || {};

  const leaveBalanceCount = data?.teacherDashboard?.leaveBalanceCount ?? 0;

  return (
    <section>
      <div className="container mt-4">
        <div className="row mt-3">
          <TopStatCard
            title="Message (NEW) Received"
            value={teacherDashboard?.newMessageCount}
            link="/messaging"
          />

          <TopStatCard
            title="Create Folder"
            icon={FaRegFolderOpen}
            link="/documentfile"
          />

          <TopStatCard
            title="Leave Balance"
            leaveBalanceCount={leaveBalanceCount}
            link="/leave/add"
          />
        </div>

        <div className="row">
          <div className="col-md-12 mb-4">
            {!loading && (
              <TeacherTimetable
                weeklySlots={teacherDashboard?.weeklySlots || []}
                setSelectedDate={setSelectedDate}
                getData={getData}
                selectedDate={selectedDate}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default TeacherDashboard;
